/**
 * Engineering Clock — contextual time controller.
 *
 * Operate → primary
 * Understand → optional (investigation playback)
 * Discover → snapshot / compare (no continuous scrub)
 *
 * syncAll is true only in replay | investigation modes — then graphs/logs/monaco/terminal update together.
 */

import { create } from 'zustand';
import type { ClockMode, ClockRole, EngineeringPhase, LifecycleStageId } from './types';

function roleForPhase(phase: EngineeringPhase): ClockRole {
  switch (phase) {
    case 'operate':
      return 'primary';
    case 'understand':
      return 'optional';
    case 'discover':
    default:
      return 'snapshot';
  }
}

function syncAllForMode(mode: ClockMode, role: ClockRole): boolean {
  if (role === 'off' || role === 'snapshot') return false;
  return mode === 'replay' || mode === 'investigation';
}

interface EngineeringClockState {
  phase: EngineeringPhase;
  role: ClockRole;
  mode: ClockMode;
  /** ISO timestamp or null when live */
  playhead: string | null;
  rangeStart: string | null;
  rangeEnd: string | null;
  /** Focused lifecycle stage (for phase pipeline highlight) */
  focusedStageId: LifecycleStageId | null;
  /** When true, scrubbing projects all bound surfaces */
  syncAll: boolean;
  isExpanded: boolean;

  setPhase: (phase: EngineeringPhase) => void;
  setMode: (mode: ClockMode) => void;
  setPlayhead: (t: string | null) => void;
  setRange: (start: string | null, end: string | null) => void;
  setFocusedStage: (id: LifecycleStageId | null) => void;
  goLive: () => void;
  enterReplay: (at?: string) => void;
  enterInvestigation: (at?: string) => void;
  exitSyncMode: () => void;
  setExpanded: (expanded: boolean) => void;
}

export const useEngineeringClock = create<EngineeringClockState>((set, get) => ({
  phase: 'operate',
  role: 'primary',
  mode: 'live',
  playhead: null,
  rangeStart: null,
  rangeEnd: null,
  focusedStageId: null,
  syncAll: false,
  isExpanded: false,

  setPhase: (phase) => {
    const role = roleForPhase(phase);
    const mode = role === 'primary' ? get().mode : role === 'snapshot' ? 'live' : get().mode;
    set({
      phase,
      role,
      mode: role === 'snapshot' ? 'live' : mode,
      syncAll: syncAllForMode(mode, role),
      // Collapse clock chrome outside Operate unless user expanded optional clock
      isExpanded: role === 'primary' ? get().isExpanded : false,
    });
  },

  setMode: (mode) => {
    const { role } = get();
    set({ mode, syncAll: syncAllForMode(mode, role), playhead: mode === 'live' ? null : get().playhead });
  },

  setPlayhead: (playhead) => {
    const { role, mode } = get();
    if (role === 'snapshot' && mode === 'live') {
      // Snapshot compare: allow setting a compare point without sync-all
      set({ playhead });
      return;
    }
    if (mode === 'live') {
      set({ mode: 'scrub', playhead, syncAll: syncAllForMode('scrub', role) });
      return;
    }
    set({ playhead });
  },

  setRange: (rangeStart, rangeEnd) => set({ rangeStart, rangeEnd }),

  setFocusedStage: (focusedStageId) => set({ focusedStageId }),

  goLive: () => set({ mode: 'live', playhead: null, syncAll: false }),

  enterReplay: (at) => {
    const { role } = get();
    if (role === 'off' || role === 'snapshot') return;
    set({
      mode: 'replay',
      playhead: at ?? get().playhead ?? new Date().toISOString(),
      syncAll: true,
      isExpanded: true,
    });
  },

  enterInvestigation: (at) => {
    const { role } = get();
    if (role === 'off') return;
    set({
      mode: 'investigation',
      playhead: at ?? get().playhead ?? new Date().toISOString(),
      syncAll: true,
      isExpanded: true,
      role: role === 'snapshot' ? 'optional' : role,
    });
  },

  exitSyncMode: () => {
    const { role } = get();
    set({
      mode: 'live',
      playhead: null,
      syncAll: false,
      isExpanded: role === 'primary',
    });
  },

  setExpanded: (isExpanded) => set({ isExpanded }),
}));

export function phaseFromPathname(pathname: string): EngineeringPhase {
  if (pathname.includes('/phase-0')) return 'discover';
  if (pathname.includes('/phase-1')) return 'understand';
  if (pathname.includes('/operate')) return 'operate';
  return 'operate';
}
